import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import DisputeDialog from '../components/DisputeDialog';
import { toast } from 'sonner';
import {
  Loader2, Package, Wallet, ArrowDownToLine, Bell, Calendar, CreditCard,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  OrdersTab, EarningsTab, DeliverDialog, WithdrawDialog, NotificationsPanel,
} from '../components/artist-dashboard';
import { WeeklyLineupDialog } from '../components/agent-dashboard/WeeklyLineupDialog';
import { LineupTab } from '../components/agent-dashboard/LineupTab';
import { WithdrawalsTab } from '../components/agent-dashboard/WithdrawalsTab';

const statusConfig = {
  pending_payment: { label: 'Pending Payment', color: 'bg-yellow-500/10 text-yellow-500' },
  paid: { label: 'Awaiting Response', color: 'bg-blue-500/10 text-blue-500' },
  accepted: { label: 'In Progress', color: 'bg-purple-500/10 text-purple-500' },
  denied: { label: 'Denied', color: 'bg-red-500/10 text-red-500' },
  delivered: { label: 'Delivered', color: 'bg-lime/10 text-lime' },
  completed: { label: 'Completed', color: 'bg-green-500/10 text-green-500' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500/10 text-gray-500' },
  refunded: { label: 'Refunded', color: 'bg-orange-500/10 text-orange-500' },
};

export default function AgentDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [profile, setProfile] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [lineup, setLineup] = useState({ artists: [], next_reset: null, week_start: null });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'orders');
  const [uploadingId, setUploadingId] = useState(null);
  const [deliverDialogOpen, setDeliverDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [lineupDialogOpen, setLineupDialogOpen] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [selectedOrderForDispute, setSelectedOrderForDispute] = useState(null);
  const fileInputRef = useRef(null);
  const deleteTimerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, earningsRes, profileRes, withdrawalsRes, lineupRes, notifsRes] = await Promise.all([
        api.getAgentOrders(),
        api.getAgentEarnings().catch(() => ({ data: null })),
        api.getAgentProfile().catch(() => ({ data: null })),
        api.getAgentWithdrawals().catch(() => ({ data: [] })),
        api.getAgentLineup().catch(() => ({ data: { artists: [], next_reset: null, week_start: null } })),
        api.getNotifications().catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data || []);
      setEarnings(earningsRes.data);
      setProfile(profileRes.data);
      setWithdrawals(withdrawalsRes.data || []);
      setLineup(lineupRes.data || { artists: [], next_reset: null, week_start: null });
      setNotifications(notifsRes.data || []);
      window.dispatchEvent(new Event('refreshBadges'));
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (activeTab !== searchParams.get('tab')) setSearchParams({ tab: activeTab });
  }, [activeTab, searchParams, setSearchParams]);

  const handleAcceptOrder = async (id) => {
    try { await api.acceptOrder(id); toast.success('Order accepted! 45 days to deliver.'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to accept order'); }
  };
  const handleDenyOrder = async (id) => {
    try { await api.denyOrder(id); toast.success('Order denied. Buyer refunded.'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to deny order'); }
  };
  const handleCancelOrder = async (id) => {
    try { await api.cancelOrder(id); toast.success('Order cancelled. Buyer refunded.'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel order'); }
  };
  const handleApproveCancelOrder = async (id) => {
    try { await api.approveCancelOrder(id); toast.success('Cancellation approved. Buyer refunded.'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve cancellation'); }
  };
  const handleDenyCancelOrder = async (id) => {
    try { await api.denyCancelOrder(id); toast.success('Cancellation denied.'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to deny cancellation'); }
  };
  const handleDeliverClick = (order) => { setSelectedOrder(order); setDeliverDialogOpen(true); };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedOrder) return;
    const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-m4a', 'audio/aac'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|aac)$/i)) {
      toast.error('Please upload an audio file (MP3, WAV, M4A, AAC)');
      return;
    }
    setUploadingId(selectedOrder.id);
    try {
      await api.deliverOrder(selectedOrder.id, file);
      toast.success('Dubplate delivered!');
      setDeliverDialogOpen(false);
      setSelectedOrder(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    setWithdrawing(true);
    try {
      await api.requestAgentWithdrawal(amount);
      toast.success('Withdrawal requested!');
      setWithdrawDialogOpen(false);
      setWithdrawAmount('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to request withdrawal');
    } finally {
      setWithdrawing(false);
    }
  };

  const handleMarkNotificationRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      window.dispatchEvent(new Event('refreshBadges'));
    } catch { toast.error('Failed to update notification'); }
  };
  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      window.dispatchEvent(new Event('refreshBadges'));
      toast.success('All notifications marked as read');
    } catch { toast.error('Failed to mark notifications as read'); }
  };
  const handleDeleteNotification = (e, id) => {
    e.stopPropagation();
    const deleted = notifications.find(n => n.id === id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    const timer = setTimeout(() => {
      api.deleteNotification(id).catch(() => {
        setNotifications(prev => [...prev, deleted].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        toast.error('Failed to delete notification');
      });
    }, 5000);
    toast('Notification deleted', {
      action: { label: 'Undo', onClick: () => { clearTimeout(timer); setNotifications(prev => [...prev, deleted].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))); } },
      duration: 5000,
    });
  };
  const handleDeleteAllNotifications = () => {
    const backup = [...notifications];
    setNotifications([]);
    if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
    deleteTimerRef.current = setTimeout(() => {
      api.deleteAllNotifications().catch(() => { setNotifications(backup); toast.error('Failed to clear notifications'); });
    }, 5000);
    toast('All notifications cleared', {
      action: { label: 'Undo', onClick: () => { clearTimeout(deleteTimerRef.current); setNotifications(backup); } },
      duration: 5000,
    });
  };

  const pendingOrders = orders.filter(o => o.status === 'paid');
  const activeOrders = orders.filter(o => o.status === 'accepted');
  const completedOrders = orders.filter(o => ['delivered', 'completed', 'cancelled', 'denied'].includes(o.status));
  const unreadNotifCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lime" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeInUp}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
          >
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-heading text-3xl font-bold">Agent Dashboard</h1>
                <Badge className="bg-lime/10 text-lime border-0">Agent</Badge>
              </div>
              <p className="text-[#A1A1AA]">Manage your orders, earnings, and payouts. Curate this week&apos;s lineup with the button on the right.</p>
            </div>
            <Button
              onClick={() => setLineupDialogOpen(true)}
              className="btn-primary"
              data-testid="weekly-lineup-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Weekly Lineup
              {lineup.artists.length > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-black/20 text-xs font-bold">{lineup.artists.length}</span>
              )}
            </Button>
          </motion.div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-surface border border-[#262626]">
              <TabsTrigger value="orders" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="agent-tab-orders">
                <Package className="w-4 h-4 mr-2" />Orders
              </TabsTrigger>
              <TabsTrigger value="lineup" className="data-[state=active]:bg-lime data-[state=active]:text-black relative" data-testid="agent-tab-lineup">
                <Calendar className="w-4 h-4 mr-2" />Lineup
                {lineup.artists.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-lime/20 text-[10px] font-bold">{lineup.artists.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="earnings" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="agent-tab-earnings">
                <Wallet className="w-4 h-4 mr-2" />Earnings
              </TabsTrigger>
              <TabsTrigger value="withdrawals" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="agent-tab-withdrawals">
                <ArrowDownToLine className="w-4 h-4 mr-2" />Withdrawals
              </TabsTrigger>
              <TabsTrigger value="notifications" className="data-[state=active]:bg-lime data-[state=active]:text-black relative" data-testid="agent-tab-notifications">
                <Bell className="w-4 h-4 mr-2" />Notifications
                {unreadNotifCount > 0 && (
                  <span className="ml-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{unreadNotifCount}</span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="orders">
              <OrdersTab
                orders={orders}
                pendingOrders={pendingOrders}
                activeOrders={activeOrders}
                completedOrders={completedOrders}
                statusConfig={statusConfig}
                handleAcceptOrder={handleAcceptOrder}
                handleDenyOrder={handleDenyOrder}
                handleDeliverClick={handleDeliverClick}
                handleCancelOrder={handleCancelOrder}
                handleApproveCancelOrder={handleApproveCancelOrder}
                handleDenyCancelOrder={handleDenyCancelOrder}
                setSelectedOrderForDispute={setSelectedOrderForDispute}
                setDisputeDialogOpen={setDisputeDialogOpen}
              />
            </TabsContent>

            <TabsContent value="lineup">
              <LineupTab lineup={lineup} onEdit={() => setLineupDialogOpen(true)} />
            </TabsContent>

            <TabsContent value="earnings">
              <EarningsTab profile={profile} earnings={earnings} setWithdrawDialogOpen={setWithdrawDialogOpen} />
            </TabsContent>

            <TabsContent value="withdrawals">
              <WithdrawalsTab profile={profile} withdrawals={withdrawals} />
            </TabsContent>

            <TabsContent value="notifications">
              <div className="bg-surface border border-[#262626] rounded-xl overflow-hidden">
                <NotificationsPanel
                  notifications={notifications}
                  handleMarkNotificationRead={handleMarkNotificationRead}
                  handleMarkAllRead={handleMarkAllRead}
                  handleDeleteNotification={handleDeleteNotification}
                  handleDeleteAllNotifications={handleDeleteAllNotifications}
                  unreadNotifCount={unreadNotifCount}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <DeliverDialog
        open={deliverDialogOpen}
        onOpenChange={setDeliverDialogOpen}
        selectedOrder={selectedOrder}
        fileInputRef={fileInputRef}
        handleFileUpload={handleFileUpload}
        uploadingId={uploadingId}
      />

      <WithdrawDialog
        open={withdrawDialogOpen}
        onOpenChange={setWithdrawDialogOpen}
        profile={profile}
        withdrawAmount={withdrawAmount}
        setWithdrawAmount={setWithdrawAmount}
        handleWithdraw={handleWithdraw}
        withdrawing={withdrawing}
      />

      <WeeklyLineupDialog
        open={lineupDialogOpen}
        onOpenChange={setLineupDialogOpen}
        onSaved={fetchData}
      />

      <DisputeDialog
        open={disputeDialogOpen}
        onOpenChange={setDisputeDialogOpen}
        order={selectedOrderForDispute}
        onDisputeCreated={fetchData}
      />
    </div>
  );
}
